import {
  Alert,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grow,
  IconButton,
  List,
  ListItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material"
import { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import EditContainer from "../../components/global/editContainer"
import EditHeading from "../../components/global/editHeading"
import PageSpinner from "../../components/global/pageSpinner"
import { MainContext } from "../../controllers/main"
import "../../styles/channelEdit.scss"
import { LanguagesContext } from "../../controllers/languages"
import { AddCircleRounded } from "@mui/icons-material"
import LoadingBackdrop from "../../components/global/loadingBackdrop"
import CantPerformActionDialog from "../../components/global/cantPerformActionDialog"
import { ChallengesContext } from "../../controllers/challenges"
import ChallengeGeneralEdit from "../../components/challenge/challengeGeneralEdit"
import ChallengeTranslationsEdit from "../../components/challenge/challengeTranslationsEdit"
import { ChallengeType } from "../../services/config/enum"
import CopyToClipboardButton from "../../components/global/copyToClipboardButton"
import ChallengeGlobalPreview from "../../components/challenge/challengeGlobalPreview"
import DuplicateChallengeDialog from "../../components/challenge/duplicateChallengeDialog"

const ChallengeEdit = () => {
  const { animation, setAnimation, setChangesSaved, showPadding } =
    useContext(MainContext)
  const { languagesForEpisodeTabs } = useContext(LanguagesContext)
  const {
    loading: controllerLoading,
    setLoading: setControllerLoading,
    editMode,
    setEditMode,
    doneChanges,
    cancelChanges,
    currentChallenge,
    getCurrentChallenge,
    preChangesCurrentChallenge,
    addTranslation,
    hasError,
    upsertChallengeParent,
    upsertChallengeDocument,
    deleteChallenge,
    autojoinTeam,
  } = useContext(ChallengesContext)
  const { challengeId } = useParams()
  const navigate = useNavigate()

  // prevent tab close
  useEffect(() => {
    const handleTabClose = (event: any) => {
      event.preventDefault()
      console.log("beforeunload event triggered")
      return (event.returnValue = "Are you sure you want to exit?")
    }

    window.addEventListener("beforeunload", handleTabClose)

    return () => {
      window.removeEventListener("beforeunload", handleTabClose)
    }
  }, [])

  // loading
  const [loading, setLoading] = useState<boolean>(true)

  // page error
  const [pageError, setPageError] = useState<boolean>(false)

  // fetch current journey
  const fetchAll = async () => {
    const noErrors = await getCurrentChallenge(challengeId)
    if (noErrors) {
      setLoading(false)
      setControllerLoading(false)
    } else {
      setPageError(true)
    }
  }

  useEffect(() => {
    fetchAll()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // tabs
  const [selectedTab, setSelectedTab] = useState<number>(0)

  // add translation dialog
  const [addTranslationOpen, setAddTranslationOpen] = useState<boolean>(false)
  const [translationToAdd, setTranslationToAdd] = useState<
    { value: string; label: string; flag?: string } | undefined
  >()

  const handleAddTranslationClose = () => {
    setAddTranslationOpen(false)
    setTimeout(() => {
      setTranslationToAdd(undefined)
    }, 150)
  }

  // no default translation alert dialog
  const [
    noDefaultTranslationAlertDialogOpen,
    setNoDefaultTranslationAlertDialogOpen,
  ] = useState<boolean>(false)

  // delete challenge dialog
  const [deleteChallengeDialogOpen, setDeleteChallengeDialogOpen] =
    useState<boolean>(false)

  const handleDeleteChallengeDialog = useCallback(() => {
    setDeleteChallengeDialogOpen((current) => !current)
  }, [])

  // team autojoined snackbar
  const [teamAutojoined, setTeamAutojoined] = useState<boolean>(false)

  // info dialog
  const [infoDialogOpen, setInfoDialogOpen] = useState<boolean>(false)

  // global preview
  const [globalPreviewOpen, setGlobalPreviewOpen] = useState<boolean>(false)
  const [currentPreviewLanguage, setCurrentPreviewLanguage] = useState<
    string | null
  >(null)

  useEffect(() => {
    if (!loading) {
      if (selectedTab === 0) {
        if (
          currentChallenge.document.items.filter((item) => item.isDefault)[0]
        ) {
          setCurrentPreviewLanguage(
            currentChallenge.document.items.filter((item) => item.isDefault)[0]
              .lang
          )
        }
      } else {
        setCurrentPreviewLanguage(
          currentChallenge.document.items[selectedTab - 1].lang
        )
      }
    }
  }, [selectedTab, loading])

  // duplicate challenge dialog
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false)

  return loading ? (
    <PageSpinner
      showBackButton={pageError}
      backButtonOnClick={() => {
        setAnimation(false)
        navigate("/challenges")
      }}
    />
  ) : (
    <EditContainer>
      <EditHeading
        saveButtonLoading={controllerLoading}
        backButtonOnClick={() => {
          setAnimation(false)
          setTimeout(() => {
            navigate("/challenges")
          }, 300)
        }}
        title={preChangesCurrentChallenge.name}
        id={preChangesCurrentChallenge.id}
        showButtons
        showEditButton
        editModeActive={editMode}
        setEditModeActive={setEditMode}
        saveButtonDisabled={!doneChanges || hasError}
        undoButtonDisabled={!doneChanges}
        doneChanges={doneChanges}
        showInfoButton={
          currentChallenge.type !== ChallengeType.reserved &&
          process.env.REACT_APP_ENV === "prod"
        }
        infoButtonOnClick={() => {
          setInfoDialogOpen(true)
        }}
        showDeleteButton
        deleteButtonOnClick={handleDeleteChallengeDialog}
        showAutojoinButton={
          preChangesCurrentChallenge.type !== ChallengeType.featured
        }
        autojoinButtonOnClick={async () => {
          setControllerLoading(true)
          const noErrors = await autojoinTeam()
          if (noErrors) {
            setTeamAutojoined(true)
          }
          setControllerLoading(false)
        }}
        undoButtonOnClick={() => {
          if (selectedTab > preChangesCurrentChallenge.document.items.length) {
            setSelectedTab(preChangesCurrentChallenge.document.items.length)
          }
          cancelChanges()
        }}
        saveButtonOnClick={async () => {
          if (
            currentChallenge.document.items.filter((item) => item.isDefault)
              .length
          ) {
            setControllerLoading(true)
            let noErrors = await upsertChallengeParent()
            if (noErrors) {
              noErrors = await upsertChallengeDocument()
              if (noErrors) {
                setControllerLoading(false)
                setChangesSaved(true)
              } else {
                setControllerLoading(false)
              }
            } else {
              setControllerLoading(false)
            }
          } else {
            setNoDefaultTranslationAlertDialogOpen(true)
          }
        }}
        showDuplicateButton
        duplicateButtonOnClick={() => setDuplicateDialogOpen(true)}
      />
      <Grow
        in={animation}
        timeout={300}
        style={{
          margin: showPadding === "yes" ? 16 : 0,
          marginTop: 0,
        }}
      >
        <Stack>
          {/* languages tabs */}
          <Stack direction="row" justifyContent="flex-start">
            <Card
              variant="outlined"
              style={{
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderTopLeftRadius: showPadding === "yes" ? null : 0,
                borderBottom: "none",
                borderLeft: showPadding === "yes" ? null : "none",
              }}
            >
              <Stack direction="row" justifyContent="flex-start">
                <Tabs
                  value={selectedTab}
                  onChange={(e: any, newValue: number) => {
                    setSelectedTab(newValue)
                  }}
                  aria-label="languages tabs"
                >
                  <Tab label="General" style={{ minWidth: 180 }} />
                  {currentChallenge.document.items.map((item) => (
                    <Tab
                      key={item.lang}
                      style={{ minWidth: 180 }}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>
                            {
                              languagesForEpisodeTabs.filter(
                                (arrayItem) => arrayItem.value === item.lang
                              )[0].flag
                            }
                          </span>
                          {currentChallenge.document.items.filter(
                            (documentItem) => documentItem.lang === item.lang
                          )[0].isDefault ? (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <span>
                                {
                                  languagesForEpisodeTabs.filter(
                                    (arrayItem) => arrayItem.value === item.lang
                                  )[0].label
                                }
                              </span>
                              <span style={{ fontSize: 10, marginTop: 2 }}>
                                (Default)
                              </span>
                            </Stack>
                          ) : (
                            <span>
                              {
                                languagesForEpisodeTabs.filter(
                                  (arrayItem) => arrayItem.value === item.lang
                                )[0].label
                              }
                            </span>
                          )}
                        </Stack>
                      }
                    />
                  ))}
                </Tabs>
                <Divider orientation="vertical" variant="middle" flexItem />
                <div
                  style={{
                    minWidth: 70,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <IconButton
                    disabled={!editMode}
                    aria-label="add language button"
                    component="span"
                    style={{ width: 48, height: 48 }}
                    onClick={() => {
                      setAddTranslationOpen(true)
                    }}
                  >
                    <AddCircleRounded />
                  </IconButton>
                </div>
              </Stack>
            </Card>
          </Stack>
          <Card style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            {!selectedTab ? (
              <ChallengeGeneralEdit
                setGlobalPreviewOpen={setGlobalPreviewOpen}
              />
            ) : (
              <ChallengeTranslationsEdit itemIndex={selectedTab - 1} />
            )}
          </Card>
        </Stack>
      </Grow>
      <LoadingBackdrop open={controllerLoading} />
      {/* add translation dialog */}
      <Dialog onClose={handleAddTranslationClose} open={addTranslationOpen}>
        <DialogTitle>Add translation</DialogTitle>
        {translationToAdd ? (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              Are you sure you want to add{" "}
              <b>{translationToAdd.label.toUpperCase()}</b>?
            </DialogContentText>
          </DialogContent>
        ) : languagesForEpisodeTabs.filter((item) => {
            if (
              item.value !== "general" &&
              !currentChallenge.document.items.filter(
                (translationItem) => translationItem.lang === item.value
              )[0]
            ) {
              return item
            }
          }).length ? (
          <List sx={{ pt: 0, minWidth: 300 }}>
            {languagesForEpisodeTabs
              .filter((item) => {
                if (
                  item.value !== "general" &&
                  !currentChallenge.document.items.filter(
                    (translationItem) => translationItem.lang === item.value
                  )[0]
                ) {
                  return item
                }
              })
              .map((item) => (
                <ListItem
                  button
                  onClick={() => {
                    setTranslationToAdd(item)
                  }}
                  key={item.value}
                >
                  <Stack direction="row" spacing={1}>
                    <span>{item.flag}</span>
                    <span>{item.label.toUpperCase()}</span>
                  </Stack>
                </ListItem>
              ))}
          </List>
        ) : (
          <DialogContent>
            <DialogContentText id="alert-dialog-description-add-translation">
              You have already added all languages available
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={handleAddTranslationClose}>Cancel</Button>
          {translationToAdd && (
            <Button
              onClick={() => {
                addTranslation(translationToAdd.value)
                handleAddTranslationClose()
                setSelectedTab(currentChallenge.document.items.length)
              }}
            >
              Add {translationToAdd.label}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* no default translation alert dialog */}
      <CantPerformActionDialog
        open={noDefaultTranslationAlertDialogOpen}
        setOpen={setNoDefaultTranslationAlertDialogOpen}
        content="There must be a default translation"
      />
      {/* delete challenge dialog */}
      <Dialog
        onClose={handleDeleteChallengeDialog}
        open={deleteChallengeDialogOpen}
      >
        <DialogTitle>Delete Challenge</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-delete-challenge">
            Are you sure you want to delete this Challenge? The operation is
            irreversible
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={controllerLoading}
            onClick={handleDeleteChallengeDialog}
          >
            Cancel
          </Button>
          <Button
            disabled={controllerLoading}
            color="error"
            onClick={async () => {
              setControllerLoading(true)

              const noErrors = await deleteChallenge(currentChallenge.id)

              if (noErrors) {
                setTimeout(() => {
                  setControllerLoading(false)
                  navigate("/challenges", { state: { challengeDeleted: true } })
                }, 1000)
              } else {
                handleDeleteChallengeDialog()
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* info dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => {
          setInfoDialogOpen(false)
        }}
        aria-labelledby="dialog-info"
        aria-describedby="dialog-action-info"
      >
        <DialogTitle id="dialog-info">About this Challenge</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>Deeplink</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  style={{
                    maxWidth: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  aworld://onesignal/challenge/{preChangesCurrentChallenge.id}
                </Typography>
                <CopyToClipboardButton
                  size="big"
                  textToCopy={`aworld://onesignal/challenge/${preChangesCurrentChallenge.id}`}
                />
              </Stack>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {preChangesCurrentChallenge.type === ChallengeType.featured
                  ? "Sharing link (replace {uid} with a specific user UID)"
                  : "Sharing link"}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  style={{
                    maxWidth: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {preChangesCurrentChallenge.type === ChallengeType.featured
                    ? `https://sharing.aworld.org/u/{uid}/c/${preChangesCurrentChallenge.id}`
                    : `https://sharing.aworld.org/t/${preChangesCurrentChallenge.team.uid}/c/${preChangesCurrentChallenge.id}`}
                </Typography>
                <CopyToClipboardButton
                  size="big"
                  textToCopy={
                    preChangesCurrentChallenge.type === ChallengeType.featured
                      ? `https://sharing.aworld.org/u/{uid}/c/${preChangesCurrentChallenge.id}`
                      : `https://sharing.aworld.org/t/${preChangesCurrentChallenge.team.uid}/c/${preChangesCurrentChallenge.id}`
                  }
                />
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              setInfoDialogOpen(false)
            }}
            autoFocus
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={teamAutojoined}
        onClose={() => {
          setTeamAutojoined(false)
        }}
        autoHideDuration={3000}
      >
        <Alert severity="success">Team autojoined</Alert>
      </Snackbar>
      {/* global preview */}
      {currentPreviewLanguage &&
      currentChallenge.document.items.filter(
        (item) => item.lang === currentPreviewLanguage
      )[0] ? (
        <ChallengeGlobalPreview
          isGlobalPreviewOpen={globalPreviewOpen}
          setIsGlobalPreviewOpen={setGlobalPreviewOpen}
          currentPreviewLanguage={currentPreviewLanguage}
          setCurrentPreviewLanguage={setCurrentPreviewLanguage}
          currentChallenge={currentChallenge}
        />
      ) : null}
      {/* duplicate challenge dialog */}
      <DuplicateChallengeDialog
        dialogOpen={duplicateDialogOpen}
        setDialogOpen={setDuplicateDialogOpen}
        handleDialogChange={() => setDuplicateDialogOpen(false)}
        challengeToDuplicate={preChangesCurrentChallenge}
      />
    </EditContainer>
  )
}

export default ChallengeEdit
